import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Bars that follow the mic's level, drawn from `level()` every frame by writing styles
 * straight onto the bars rather than re-rendering: a meter at 60fps through React state
 * would re-render the composer sixty times a second.
 */
export function LevelMeter({ level, bars = 18, className }: { level: () => number; bars?: number; className?: string }) {
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const history: number[] = Array(bars).fill(0);
    let frame = 0;
    let last = 0;
    const tick = (time: number) => {
      frame = requestAnimationFrame(tick);
      if (time - last < 60) return;
      last = time;
      history.shift();
      history.push(level());
      const children = box.current?.children;
      if (!children) return;
      for (let i = 0; i < children.length; i++) {
        (children[i] as HTMLElement).style.transform = `scaleY(${Math.max(0.12, Math.min(1, history[i] ?? 0))})`;
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [level, bars]);
  return (
    <span ref={box} aria-hidden className={cn("flex h-5 items-center gap-[3px]", className)}>
      {Array.from({ length: bars }, (_, i) => (
        <span key={i} className="h-full w-[3px] origin-center scale-y-[0.12] rounded-full bg-current transition-transform duration-75" />
      ))}
    </span>
  );
}

/** m:ss since `startedAt`, ticking. */
export function Elapsed({ startedAt, className }: { startedAt: number; className?: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(handle);
  }, []);
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return (
    <span className={cn("tabular-nums", className)}>
      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
    </span>
  );
}
